import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TrackProvider } from "@/context/TrackContext";
import Layout from "@/components/Layout";
import JobDetail from "@/pages/JobDetail";

const Login = lazy(() => import("@/pages/Login"));
const Overview = lazy(() => import("@/pages/Overview"));
const Discover = lazy(() => import("@/pages/Discover"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const CareerMaster = lazy(() => import("@/pages/CareerMaster"));
const Resumes = lazy(() => import("@/pages/Resumes"));
const ATSLab = lazy(() => import("@/pages/ATSLab"));
const CoverLetters = lazy(() => import("@/pages/CoverLetters"));
const Companies = lazy(() => import("@/pages/Companies"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const Interviews = lazy(() => import("@/pages/Interviews"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const Settings = lazy(() => import("@/pages/Settings"));
const AdminPackage = lazy(() => import("@/pages/AdminPackage"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const DocumentComparison = lazy(() => import("@/pages/DocumentComparison"));

const loadingScreen = <p role="status" className="p-4 text-sm text-muted-foreground">Loading screen…</p>;

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
        Loading EzJOB!…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Suspense fallback={loadingScreen}><Login /></Suspense>} />
      <Route
        path="/*"
        element={
          <Protected>
            <Layout>
              <Suspense fallback={loadingScreen}>
              <Routes>
                <Route path="/" element={<Overview />} />
                <Route path="/discover" element={<Discover />} />
                <Route path="/jobs/:id" element={<JobDetail />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/career" element={<CareerMaster />} />
                <Route path="/resumes" element={<Resumes />} />
                <Route path="/ats" element={<ATSLab />} />
                <Route path="/cover-letters" element={<CoverLetters />} />
                <Route path="/document-comparison/:kind" element={<DocumentComparison />} />
                <Route path="/companies" element={<Companies />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/interviews" element={<Interviews />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/admin/package" element={<AdminPackage />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </Layout>
          </Protected>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TrackProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </TrackProvider>
    </AuthProvider>
  );
}
